/* SpawnAnything — sprite impostors for far units.
   At army setup the near-LOD model (with the real vertex-shader walk animation) is rendered once into two small atlases:
   albedo (sRGB, alpha = coverage) and view-space normal (+ glow in alpha), for 8 yaw directions × 3 elevations ×
   (6 walk phases + 1 idle pose). Far units are then drawn as ONE instanced draw of camera-facing quads that pick their
   cell from the relative yaw / elevation to the camera and the unit's walk phase. Lighting is not baked: the quads are
   a MeshToonMaterial whose normal comes from the atlas, so sun, hemisphere light, torches, fog and received shadows all
   behave like on the real meshes. The same billboard code in the shadow pass faces the sun and picks the cell the sun
   sees, so impostors also cast proper silhouette shadows. Comic outlines come from the screen-space pass (depth). */
import * as THREE from 'three';

export const IMP_YAW = 8, IMP_WALK = 6, IMP_FR = IMP_WALK + 1, IMP_EL = [0.17, 0.55, 0.95]; // elevations in radians (~10°, 32°, 54°)
const CELLS = IMP_YAW * IMP_EL.length * IMP_FR, COLS = 16, ROWS = Math.ceil(CELLS / COLS);

const BAKE_V = /* glsl */`
varying vec3 vCol; varying vec3 vN;
void main() {
  vec3 p = position; vec3 n = normal; animUnit(p, n);
  vCol = color; vN = normalize(mat3(instanceMatrix) * n); vEmit = aEmit;
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * instanceMatrix * vec4(p, 1.0);
}`;

/**
 * Bake the atlases for one army. A needs geo0 (near LOD), h, pivots/roles/bases, aim, atkStyle, stats.s, m.waveAmp.
 * animHead: the GLSL that declares the unit animation (uniforms, aPart/aAnim/aEmit attributes, animUnit()).
 */
export function bakeImpostor(renderer, A, animHead, cellPx = 64) {
  const src = A.geo0, pos = src.getAttribute('position');
  let cy = 0, ymax = 0; for (let i = 0; i < pos.count; i++) ymax = Math.max(ymax, pos.getY(i));
  cy = ymax / 2;
  let R = 0;
  for (let i = 0; i < pos.count; i++) R = Math.max(R, Math.hypot(pos.getX(i), pos.getY(i) - cy, pos.getZ(i)));
  R *= 1.12; // swinging arms / weapons leave the rest-pose bounds a little
  const g = new THREE.BufferGeometry();
  for (const k of ['position', 'normal', 'color', 'aPart', 'aEmit']) g.setAttribute(k, src.getAttribute(k));
  g.setIndex(src.index);
  const anim = new THREE.InstancedBufferAttribute(new Float32Array(CELLS * 4), 4);
  g.setAttribute('aAnim', anim);
  const uni = {
    uPivot: { value: A.pivots }, uRole: { value: A.roles }, uBase: { value: A.bases }, uAim: { value: A.aim ? 1 : 0 }, uAtk: { value: A.atkStyle },
    uRecoil: { value: 0 }, uTime: { value: 0 }, uWave: { value: A.m.waveAmp || 0 },
  };
  const colMat = new THREE.ShaderMaterial({ uniforms: uni, vertexColors: true, vertexShader: animHead + BAKE_V, fragmentShader: 'varying vec3 vCol; varying vec3 vN; void main(){ gl_FragColor = vec4(vCol, 1.0); }' });
  const nrmMat = new THREE.ShaderMaterial({ uniforms: uni, vertexColors: true, vertexShader: animHead + BAKE_V, fragmentShader: 'varying vec3 vCol; varying vec3 vN; varying float vEmit; void main(){ gl_FragColor = vec4(normalize(vN) * 0.5 + 0.5, vEmit); }' });
  const im = new THREE.InstancedMesh(g, colMat, CELLS); im.frustumCulled = false;
  const M = new THREE.Matrix4(), T = new THREE.Matrix4(), Rx = new THREE.Matrix4(), Ry = new THREE.Matrix4(), T0 = new THREE.Matrix4().makeTranslation(0, -cy, 0);
  for (let e = 0; e < IMP_EL.length; e++) for (let y = 0; y < IMP_YAW; y++) for (let f = 0; f < IMP_FR; f++) {
    const cell = (e * IMP_YAW + y) * IMP_FR + f, col = cell % COLS, row = (cell / COLS) | 0;
    // camera looks down -z: tilting the model's top towards +z by e = viewing it from e above the horizon
    M.copy(T.makeTranslation((col + 0.5) * 2 * R, -(row + 0.5) * 2 * R, 0)).multiply(Rx.makeRotationX(IMP_EL[e])).multiply(Ry.makeRotationY(y * Math.PI * 2 / IMP_YAW)).multiply(T0);
    im.setMatrixAt(cell, M);
    const walk = f < IMP_WALK ? 1 : 0, ph = f < IMP_WALK ? f * Math.PI * 2 / IMP_WALK : 0;
    anim.array.set([walk, ph, -1, 0], cell * 4);
  }
  const scene = new THREE.Scene(); scene.add(im);
  const cam = new THREE.OrthographicCamera(0, COLS * 2 * R, 0, -ROWS * 2 * R, 0.1, 10 * R + 10); cam.position.z = 5 * R + 5; cam.updateMatrixWorld();
  const mk = srgb => {
    const rt = new THREE.WebGLRenderTarget(COLS * cellPx, ROWS * cellPx, { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: false, depthBuffer: true });
    if (srgb) rt.texture.colorSpace = THREE.SRGBColorSpace;
    return rt;
  };
  const colRT = mk(true), nrmRT = mk(false);
  const prevRT = renderer.getRenderTarget(), prevCol = renderer.getClearColor(new THREE.Color()), prevA = renderer.getClearAlpha(), prevSh = renderer.shadowMap.autoUpdate;
  renderer.shadowMap.autoUpdate = false;
  renderer.setClearColor(0x000000, 0);
  for (const [rt, mat] of [[colRT, colMat], [nrmRT, nrmMat]]) { im.material = mat; renderer.setRenderTarget(rt); renderer.clear(); renderer.render(scene, cam); }
  renderer.setRenderTarget(prevRT); renderer.setClearColor(prevCol, prevA); renderer.shadowMap.autoUpdate = prevSh;
  g.deleteAttribute('aAnim'); anim.array = new Float32Array(0); colMat.dispose(); nrmMat.dispose(); im.dispose();
  return { colRT, nrmRT, R, cy, dispose() { colRT.dispose(); nrmRT.dispose(); } };
}

// billboard + cell selection (vertex); works for the colour pass (camera) and the shadow pass (sun) alike
const IMP_V_HEAD = /* glsl */`
attribute vec4 aImpA; attribute vec4 aImpB; uniform vec4 uImp; uniform vec3 uImpEl; varying vec2 vImpUv;
vec3 impBillboard(vec3 q) {
  float s = aImpB.x;
  vec3 ctr = aImpA.xyz + vec3(0.0, uImp.y * s, 0.0);
  vec3 toC = cameraPosition - ctr;
  float rel = aImpA.w - atan(toC.x, toC.z);
  float yi = mod(floor(rel * ${(IMP_YAW / (Math.PI * 2)).toFixed(6)} + 0.5), ${IMP_YAW.toFixed(1)});
  float el = atan(toC.y, length(toC.xz));
  float ei = el < ${((IMP_EL[0] + IMP_EL[1]) / 2).toFixed(4)} ? 0.0 : el < ${((IMP_EL[1] + IMP_EL[2]) / 2).toFixed(4)} ? 1.0 : 2.0;
  float fi = aImpB.y < 0.25 ? ${IMP_WALK.toFixed(1)} : mod(floor(aImpB.z * ${(IMP_WALK / (Math.PI * 2)).toFixed(6)} + 0.5), ${IMP_WALK.toFixed(1)});
  float cell = (ei * ${IMP_YAW.toFixed(1)} + yi) * ${IMP_FR.toFixed(1)} + fi;
  float col = mod(cell, uImp.z), row = floor(cell / uImp.z);
  vImpUv = vec2((col + q.x + 0.5) / uImp.z, 1.0 - (row + 0.5 - q.y) / uImp.w);
  vec3 right = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]), up = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
  return ctr + (right * q.x + up * q.y) * (2.0 * uImp.x * s);
}`;

/** Instanced impostor quads for one army (cap instances). Fill aA = (x, y, z, yaw), aB = (scale, walk, phase, 0); set count. */
export function makeImpostorMesh(bake, cap, toonGrad) {
  const quad = new THREE.PlaneGeometry(1, 1);
  const g = new THREE.InstancedBufferGeometry();
  g.index = quad.index; g.setAttribute('position', quad.getAttribute('position')); g.setAttribute('normal', quad.getAttribute('normal'));
  const aA = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4), aB = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
  aA.setUsage(THREE.DynamicDrawUsage); aB.setUsage(THREE.DynamicDrawUsage);
  g.setAttribute('aImpA', aA); g.setAttribute('aImpB', aB); g.instanceCount = 0;
  const uni = {
    uImp: { value: new THREE.Vector4(bake.R, bake.cy, COLS, ROWS) }, uImpEl: { value: new THREE.Vector3(...IMP_EL) },
    uImpCol: { value: bake.colRT.texture }, uImpNrm: { value: bake.nrmRT.texture },
  };
  const mat = new THREE.MeshToonMaterial({ gradientMap: toonGrad });
  mat.onBeforeCompile = sh => {
    Object.assign(sh.uniforms, uni);
    sh.vertexShader = sh.vertexShader.replace('void main() {', IMP_V_HEAD + '\nvoid main() {')
      .replace('#include <begin_vertex>', 'vec3 transformed = impBillboard(position);');
    sh.fragmentShader = sh.fragmentShader.replace('void main() {', 'uniform sampler2D uImpCol, uImpNrm; varying vec2 vImpUv;\nvoid main() {')
      .replace('#include <color_fragment>', 'vec4 impC = texture2D(uImpCol, vImpUv); if (impC.a < 0.5) discard; diffuseColor.rgb = impC.rgb; vec4 impN = texture2D(uImpNrm, vImpUv);')
      .replace('#include <normal_fragment_begin>', 'float faceDirection = 1.0; vec3 normal = normalize(impN.xyz * 2.0 - 1.0); vec3 nonPerturbedNormal = normal;')
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += diffuseColor.rgb * step(0.5, impN.a) * 1.6;');
  };
  mat.customProgramCacheKey = () => 'unit-imp';
  const depth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
  depth.onBeforeCompile = sh => {
    Object.assign(sh.uniforms, uni);
    sh.vertexShader = sh.vertexShader.replace('void main() {', IMP_V_HEAD + '\nvoid main() {')
      .replace('#include <begin_vertex>', 'vec3 transformed = impBillboard(position);');
    sh.fragmentShader = sh.fragmentShader.replace('void main() {', 'uniform sampler2D uImpCol; varying vec2 vImpUv;\nvoid main() {\nif (texture2D(uImpCol, vImpUv).a < 0.5) discard;');
  };
  depth.customProgramCacheKey = () => 'unit-imp-depth';
  const mesh = new THREE.Mesh(g, mat); mesh.frustumCulled = false; mesh.customDepthMaterial = depth;
  return {
    mesh, aA, aB, cap,
    /** upload the first n instances */
    commit(n) {
      g.instanceCount = n; mesh.visible = n > 0;
      if (!n) return;
      for (const a of [aA, aB]) { a.clearUpdateRanges(); a.addUpdateRange(0, n * 4); a.needsUpdate = true; }
    },
    dispose() { g.dispose(); quad.dispose(); mat.dispose(); depth.dispose(); bake.dispose(); },
  };
}
